import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const WalletSchema = z.object({
  provider_wallet_id: z.string().min(5, 'Wallet ID is required'),
  wallet_passphrase: z.string().min(5, 'Passphrase is required'),
  name: z.string().min(5, 'Wallet Name is required'),
});

export type WalletSchemaType = z.infer<typeof WalletSchema>;

export type WalletFormProps = {
  onSubmit: (data: WalletSchemaType) => Promise<any>;
  onCancel?: () => any;
  data?: WalletSchemaType;
  disabled?: boolean;
};

export const WalletForm = ({ onSubmit, onCancel, data, disabled }: WalletFormProps) => {
  const isEdit = useMemo(() => !!data, [data]);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<WalletSchemaType>({
    resolver: zodResolver(WalletSchema),
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (data) {
      setValue('provider_wallet_id', data.provider_wallet_id);
      setValue('name', data.name);
    }
  }, [data]);

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={2} p={3}>
      <TextField
        disabled={disabled || isEdit}
        label="Wallet Name *"
        size="small"
        fullWidth
        error={!!errors.name}
        helperText={errors.name?.message}
        {...register('name')}
      />
      <TextField
        disabled={disabled || isEdit}
        label="Wallet ID *"
        size="small"
        fullWidth
        error={!!errors.provider_wallet_id}
        helperText={errors.provider_wallet_id?.message}
        {...register('provider_wallet_id')}
      />
      <TextField
        disabled={disabled}
        label="Wallet Passphrase *"
        size="small"
        fullWidth
        autoComplete="off"
        placeholder={isEdit && disabled ? data?.wallet_passphrase : ''}
        error={!!errors.wallet_passphrase}
        helperText={errors.wallet_passphrase?.message}
        {...register('wallet_passphrase')}
      />
      {!disabled && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2}>
          {isSubmitting && <Typography variant="body2">Saving changes…</Typography>}
          {isEdit && <Button onClick={onCancel}>Cancel</Button>}
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {isEdit ? 'Save Settings' : 'Activate Wallet'}
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
};
